<script lang="ts">
  /**
   * Shared modal shell — focus trap, restore, dialog ARIA, Escape, backdrop.
   * P2 #1 from the 2026-05-23 codex audit.
   */
  import { onMount } from 'svelte'
  import { trapFocus } from '../lib/focusTrap'

  interface Props {
    labelledBy?: string
    label?: string
    variant?: 'center' | 'drawer' | 'palette'
    elevated?: boolean
    onClose: () => void
    children?: import('svelte').Snippet
  }

  let {
    labelledBy,
    label,
    variant = 'center',
    elevated = false,
    onClose,
    children,
  }: Props = $props()

  let dialogEl = $state<HTMLElement | undefined>()

  onMount(() => {
    const previous = document.activeElement
    if (dialogEl) return trapFocus(dialogEl, previous)
    return undefined
  })

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }
</script>

<svelte:window onkeydown={onKey} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="backdrop"
  class:drawer={variant === 'drawer'}
  class:palette={variant === 'palette'}
  class:elevated
  onclick={onClose}
  role="presentation"
>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <div
    bind:this={dialogEl}
    class="shell"
    class:drawer={variant === 'drawer'}
    class:palette={variant === 'palette'}
    onclick={(e) => e.stopPropagation()}
    role="dialog"
    aria-modal="true"
    aria-labelledby={labelledBy}
    aria-label={labelledBy ? undefined : label}
    tabindex="-1"
  >
    {@render children?.()}
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(8, 6, 4, 0.42);
    display: grid;
    place-items: center;
    z-index: 50;
    animation: fade-in var(--motion-base) var(--ease-out);
  }
  .backdrop.drawer {
    display: flex;
    justify-content: flex-end;
    align-items: stretch;
    background: rgba(8, 6, 4, 0.32);
  }
  .backdrop.palette {
    display: grid;
    place-items: start center;
    padding-top: 14vh;
    z-index: 60;
  }
  .backdrop.elevated {
    z-index: 70;
  }
  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  .shell {
    background: var(--bg-surface);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-xl);
    color: var(--text-primary);
    animation: dialog-in var(--motion-base) var(--ease-spring);
  }
  .shell.drawer {
    position: relative;
    height: 100%;
    width: min(380px, 100vw);
    max-width: 100%;
    border-radius: 0;
    border-left: 1px solid var(--border-subtle);
    border-top: none;
    border-right: none;
    border-bottom: none;
    animation: slide-in var(--motion-slow) var(--ease-spring);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .shell.palette {
    width: min(580px, 92vw);
    overflow: hidden;
    animation: drop-in var(--motion-base) var(--ease-spring);
  }
  @keyframes dialog-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes slide-in {
    from {
      transform: translateX(20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes drop-in {
    from {
      opacity: 0;
      transform: translateY(-12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
